import type { ZodType } from 'zod';
import { LoadedFirmContextSchema } from './playbooks.js';
import {
  SuccessfulRunListSchema,
  SuccessfulRunLookupSchema,
  SuccessfulRunMutationSchema,
} from './successfulRuns.js';
import {
  AnnotationListSchema,
  AnnotationSchema,
  AnnotatedConversationListSchema,
  AnnotationMutationSchema,
} from './annotations.js';
import { StockPartSearchSchema } from './stockParts.js';
import {
  ReferenceDesignListSchema,
  ReferenceDesignDetailSchema,
  ReferenceDesignHealthSchema,
} from './referenceDesigns.js';
import { ZemaxBridgeListSchema } from './zemaxBridges.js';
import {
  CollabStateSchema,
  CollaboratorListSchema,
  EditProposalListSchema,
  EditProposalMutationSchema,
} from './collab.js';
import { ZemaxQueueSchema } from './zemaxQueue.js';
import { ContextCompactionListSchema } from './contextCompactions.js';
import { CompletedToursSchema } from './tours.js';
import { ZemaxWorkerCallListSchema } from './zemaxWorkerCalls.js';
import {
  OctopusPingSchema,
  OctopusRunStartedSchema,
  OctopusJobSchema,
  OctopusCancelSchema,
} from './octopus.js';
import {
  OptomechAvailabilitySchema,
  OptomechCaseSystemSchema,
  OptomechCompareSchema,
  OptomechRunListSchema,
  OptomechRunSchema,
  OptomechSurfaceSchema,
  OptomechVolumeSchema,
} from './optomech.js';
import {
  OptomechBodyBindingSchema,
  OptomechBodyListSchema,
  OptomechMaterialListSchema,
  OptomechMaterialRowSchema,
  OptomechMaterialVersionListSchema,
} from './optomechMaterials.js';
import { OptomechPartSearchSchema, OptomechThreadListSchema } from './optomechParts.js';
import { OptomechDesignElementListSchema } from './optomechElements.js';
import { OptomechAssemblyGeometryResponseSchema, OptomechModelListSchema, OptomechModelRowSchema, OptomechRetainedMeshResponseSchema, OptomechViewBodiesResponseSchema } from './optomechAssembly.js';
import { MacroRunResponseSchema } from './zosapiMacros.js';
import {
  OptilandMeritCurrentSchema,
  OptilandMeritHistorySchema,
  OptilandMeritRestoreSchema,
  OptilandMeritSaveSchema,
  OptilandOptimizeRunSchema,
} from './optilandMerit.js';
import { CONVERSATION_STREAM_EVENTS } from './sseConversation.js';
import { TURN_STREAM_EVENTS } from './sseTurn.js';

export interface RouteContract {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Public path exactly as registered in Fastify (prefix included). */
  path: string;
  /** Basename of the owning file in src/server/routes/ — used by the coverage check. */
  routeFile: string;
  /** 200-response schema. */
  response: ZodType;
  /** The whole operation is on its way out: rendered as `deprecated: true` (no `x-sunset`), which is what
   *  lets the next PR delete it. `oasdiff breaking` (scripts/contract-gate.mjs) reports a deleted operation
   *  as ERR unless main's artifact marks it deprecated; with the flag and no sunset date the deletion is
   *  INFO, below the WARN floor `breaking` reports. Still served until that PR. */
  deprecated?: true;
}

export const ROUTE_MANIFEST: RouteContract[] = [
  { method: 'GET', path: '/optiland-merit/current', routeFile: 'optilandMerit.ts', response: OptilandMeritCurrentSchema },
  { method: 'POST', path: '/optiland-merit/save', routeFile: 'optilandMerit.ts', response: OptilandMeritSaveSchema },
  { method: 'GET', path: '/optiland-merit/history', routeFile: 'optilandMerit.ts', response: OptilandMeritHistorySchema },
  { method: 'POST', path: '/optiland-merit/restore', routeFile: 'optilandMerit.ts', response: OptilandMeritRestoreSchema },
  { method: 'POST', path: '/optiland-merit/run', routeFile: 'optilandMerit.ts', response: OptilandOptimizeRunSchema },
  { method: 'GET', path: '/conversations/{conversationId}/collab', routeFile: 'collab.ts', response: CollabStateSchema },
  { method: 'POST', path: '/conversations/{conversationId}/presence', routeFile: 'collab.ts', response: CollabStateSchema },
  { method: 'GET', path: '/conversations/{conversationId}/collaborators', routeFile: 'collab.ts', response: CollaboratorListSchema },
  { method: 'GET', path: '/conversations/{conversationId}/proposals', routeFile: 'collab.ts', response: EditProposalListSchema },
  { method: 'POST', path: '/conversations/{conversationId}/proposals', routeFile: 'collab.ts', response: EditProposalMutationSchema },
  { method: 'POST', path: '/proposals/{proposalId}/approve', routeFile: 'collab.ts', response: EditProposalMutationSchema },
  { method: 'POST', path: '/proposals/{proposalId}/reject', routeFile: 'collab.ts', response: EditProposalMutationSchema },
  { method: 'GET', path: '/playbooks/loaded', routeFile: 'playbooks.ts', response: LoadedFirmContextSchema },
  { method: 'GET', path: '/admin/zemax-bridges', routeFile: 'admin/zemaxBridges.ts', response: ZemaxBridgeListSchema },
  { method: 'GET', path: '/admin/zemax-worker-calls', routeFile: 'admin/zemaxWorkerCalls.ts', response: ZemaxWorkerCallListSchema },
  { method: 'GET', path: '/admin/zemax-queue', routeFile: 'admin/zemaxQueue.ts', response: ZemaxQueueSchema },
  { method: 'GET', path: '/zemax/worker-calls', routeFile: 'zemaxWorkerCalls.ts', response: ZemaxWorkerCallListSchema },
  { method: 'GET', path: '/admin/successful-runs', routeFile: 'admin/successfulRuns.ts', response: SuccessfulRunListSchema },
  { method: 'GET', path: '/admin/successful-runs/{conversationId}', routeFile: 'admin/successfulRuns.ts', response: SuccessfulRunLookupSchema },
  { method: 'POST', path: '/admin/successful-runs', routeFile: 'admin/successfulRuns.ts', response: SuccessfulRunMutationSchema },
  { method: 'DELETE', path: '/admin/successful-runs/{conversationId}', routeFile: 'admin/successfulRuns.ts', response: SuccessfulRunMutationSchema },
  { method: 'GET', path: '/admin/conversations/{conversationId}/compactions', routeFile: 'admin/contextCompactions.ts', response: ContextCompactionListSchema },
  { method: 'GET', path: '/users/tours', routeFile: 'userTours.ts', response: CompletedToursSchema },
  { method: 'POST', path: '/users/tours/{tourId}', routeFile: 'userTours.ts', response: CompletedToursSchema },
  { method: 'GET', path: '/admin/annotations', routeFile: 'admin/annotations.ts', response: AnnotatedConversationListSchema },
  { method: 'GET', path: '/admin/conversations/{conversationId}/annotations', routeFile: 'admin/annotations.ts', response: AnnotationListSchema },
  { method: 'POST', path: '/admin/conversations/{conversationId}/annotations', routeFile: 'admin/annotations.ts', response: AnnotationSchema },
  { method: 'PATCH', path: '/admin/annotations/{id}', routeFile: 'admin/annotations.ts', response: AnnotationSchema },
  { method: 'DELETE', path: '/admin/annotations/{id}', routeFile: 'admin/annotations.ts', response: AnnotationMutationSchema },
  { method: 'GET', path: '/stock-parts/search', routeFile: 'stockParts.ts', response: StockPartSearchSchema },
  { method: 'GET', path: '/admin/reference-designs', routeFile: 'admin/referenceDesigns.ts', response: ReferenceDesignListSchema },
  { method: 'GET', path: '/admin/reference-designs/health', routeFile: 'admin/referenceDesigns.ts', response: ReferenceDesignHealthSchema },
  { method: 'GET', path: '/admin/reference-designs/{id}', routeFile: 'admin/referenceDesigns.ts', response: ReferenceDesignDetailSchema },
  { method: 'GET', path: '/admin/octopus/ping', routeFile: 'admin/octopus.ts', response: OctopusPingSchema },
  { method: 'POST', path: '/admin/octopus/run', routeFile: 'admin/octopus.ts', response: OctopusRunStartedSchema },
  { method: 'GET', path: '/admin/octopus/result/{jobId}', routeFile: 'admin/octopus.ts', response: OctopusJobSchema },
  { method: 'POST', path: '/admin/octopus/cancel', routeFile: 'admin/octopus.ts', response: OctopusCancelSchema },
  { method: 'GET', path: '/optomech/availability', routeFile: 'optomech.ts', response: OptomechAvailabilitySchema },
  { method: 'POST', path: '/optomech/runs', routeFile: 'optomech.ts', response: OptomechRunSchema },
  { method: 'GET', path: '/optomech/runs', routeFile: 'optomech.ts', response: OptomechRunListSchema },
  { method: 'GET', path: '/optomech/runs/{runId}', routeFile: 'optomech.ts', response: OptomechRunSchema },
  { method: 'GET', path: '/optomech/runs/{runId}/cases/{loadCase}', routeFile: 'optomech.ts', response: OptomechCaseSystemSchema },
  { method: 'GET', path: '/optomech/runs/{runId}/cases/{loadCase}/compare', routeFile: 'optomech.ts', response: OptomechCompareSchema },
  { method: 'GET', path: '/optomech/runs/{runId}/surface', routeFile: 'optomech.ts', response: OptomechSurfaceSchema },
  { method: 'GET', path: '/optomech/runs/{runId}/volume', routeFile: 'optomech.ts', response: OptomechVolumeSchema },
  { method: 'GET', path: '/optomech/materials', routeFile: 'optomechLibrary.ts', response: OptomechMaterialListSchema },
  { method: 'POST', path: '/optomech/materials', routeFile: 'optomechLibrary.ts', response: OptomechMaterialRowSchema },
  { method: 'GET', path: '/optomech/materials/{materialId}/versions', routeFile: 'optomechLibrary.ts', response: OptomechMaterialVersionListSchema },
  { method: 'POST', path: '/optomech/materials/{materialId}/versions', routeFile: 'optomechLibrary.ts', response: OptomechMaterialRowSchema },
  { method: 'GET', path: '/optomech/bodies', routeFile: 'optomechLibrary.ts', response: OptomechBodyListSchema },
  { method: 'PUT', path: '/optomech/bodies/binding', routeFile: 'optomechLibrary.ts', response: OptomechBodyBindingSchema },
  { method: 'GET', path: '/optomech/parts', routeFile: 'optomechParts.ts', response: OptomechPartSearchSchema },
  { method: 'GET', path: '/optomech/threads', routeFile: 'optomechParts.ts', response: OptomechThreadListSchema },
  { method: 'GET', path: '/optomech/elements', routeFile: 'optomechParts.ts', response: OptomechDesignElementListSchema },
  { method: 'POST', path: '/optomech/models', routeFile: 'optomechModels.ts', response: OptomechModelRowSchema },
  { method: 'GET', path: '/optomech/models', routeFile: 'optomechModels.ts', response: OptomechModelListSchema },
  { method: 'GET', path: '/optomech/models/{modelId}', routeFile: 'optomechModels.ts', response: OptomechModelRowSchema },
  { method: 'POST', path: '/optomech/models/{modelId}/build', routeFile: 'optomechModels.ts', response: OptomechModelRowSchema },
  { method: 'GET', path: '/optomech/geometry', routeFile: 'optomechModels.ts', response: OptomechAssemblyGeometryResponseSchema, deprecated: true },
  { method: 'GET', path: '/optomech/retained-mesh', routeFile: 'optomechModels.ts', response: OptomechRetainedMeshResponseSchema, deprecated: true },
  { method: 'GET', path: '/optomech/view-bodies', routeFile: 'optomechModels.ts', response: OptomechViewBodiesResponseSchema },
  { method: 'POST', path: '/zosapi-macros/run', routeFile: 'zosapiMacros.ts', response: MacroRunResponseSchema },
];

export interface SseStreamContract {
  /** Public path as registered in Fastify, OpenAPI-style params. */
  path: string;
  /** Basename of the owning file in src/server/routes/. */
  routeFile: string;
  /** Wire event name → payload schema. Rendered as a synthetic envelope object
   *  in the OpenAPI artifact so oasdiff sees event/field removals as ordinary
   *  property removals (and `.meta({ deprecated: true })` flows through for the
   *  two-PR removal dance). The envelope is a GATE ARTIFACT, not a wire shape —
   *  on the wire each event is a separate SSE frame. */
  events: Record<string, ZodType>;
}

export const SSE_STREAM_MANIFEST: SseStreamContract[] = [
  { path: '/jobs/stream', routeFile: 'jobs.ts', events: TURN_STREAM_EVENTS },
  { path: '/conversations/{conversation_id}/stream', routeFile: 'conversations.ts', events: CONVERSATION_STREAM_EVENTS },
];
